// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Field-level audit logging — answers "who changed this number, and when".
//
// All writes go through the record_audit_event SECURITY DEFINER RPC
// (migration 024): the server validates the action enum, verifies the caller
// can access the project, truncates values, and builds metadata itself.
// Fire-and-forget: audit failures must never surface to or block the user.

import { supabase } from "./supabase";

export type AuditAction =
  | "project.create" | "project.save" | "project.delete" | "project.restore" | "project.update"
  | "snapshot.create" | "snapshot.delete" | "snapshot.restore" | "snapshot.rename"
  | "share.create" | "share.revoke"
  | "account.export" | "account.deleted"
  | "item.create" | "item.update" | "item.delete"
  | "markup.update"
  | "bid.accept" | "bid.award"
  | "export.csv" | "export.pdf" | "export.docx" | "export.procore";

export interface AuditDetail {
  field?: string;       // e.g. "unitCost", "qty", "overhead"
  oldValue?: string | number | null;
  newValue?: string | number | null;
  itemDesc?: string;    // human-readable item description for the activity feed
}

/** Record an audit event. No-ops silently when Supabase isn't configured. */
export function auditEvent(
  action: AuditAction,
  resourceType: "project" | "snapshot" | "share" | "account",
  resourceId?: string | null,
  detail?: AuditDetail,
): void {
  if (!supabase || (resourceType === "project" && !resourceId)) return;
  supabase
    .rpc("record_audit_event", {
      p_action: action,
      p_resource_type: resourceType,
      p_resource_id: resourceId ?? null,
      p_field: detail?.field ?? null,
      p_old_value: detail?.oldValue != null ? String(detail.oldValue) : null,
      p_new_value: detail?.newValue != null ? String(detail.newValue) : null,
      p_item_desc: detail?.itemDesc ?? null,
    })
    .then(({ error }) => {
      if (error) console.warn("[audit]", action, error.message);
    });
}

/**
 * Debounced field-change auditing. Rapid successive edits to the same
 * item+field within the window collapse into one entry that preserves the
 * ORIGINAL old value and the FINAL new value — so tabbing through a cell
 * twice doesn't produce two log lines, but the real before/after survives.
 */
const pending = new Map<string, { action: AuditAction; resourceId: string; detail: AuditDetail; timer: ReturnType<typeof setTimeout> }>();
const DEBOUNCE_MS = 4000;

export function auditFieldChange(
  projectId: string | null | undefined,
  itemId: string,
  field: string,
  oldValue: string | number | null | undefined,
  newValue: string | number | null | undefined,
  itemDesc?: string,
): void {
  if (!supabase || !projectId) return;
  if (oldValue === newValue) return;
  const key = `${projectId}|${itemId}|${field}`;
  const existing = pending.get(key);
  if (existing) {
    clearTimeout(existing.timer);
    existing.detail.newValue = newValue ?? null;   // keep original oldValue
    if (itemDesc) existing.detail.itemDesc = itemDesc;
    existing.timer = setTimeout(() => flush(key), DEBOUNCE_MS);
  } else {
    pending.set(key, {
      action: "item.update",
      resourceId: projectId,
      detail: { field, oldValue: oldValue ?? null, newValue: newValue ?? null, itemDesc },
      timer: setTimeout(() => flush(key), DEBOUNCE_MS),
    });
  }
}

function flush(key: string): void {
  const entry = pending.get(key);
  if (!entry) return;
  pending.delete(key);
  // Collapsed no-op (e.g. 5 → 8 → 5): skip entirely
  if (entry.detail.oldValue === entry.detail.newValue) return;
  auditEvent(entry.action, "project", entry.resourceId, entry.detail);
}

/** Flush all pending field changes immediately (e.g. before unload). */
export function flushAuditQueue(): void {
  for (const key of [...pending.keys()]) {
    const entry = pending.get(key);
    if (entry) clearTimeout(entry.timer);
    flush(key);
  }
}
